import type { RefObject } from 'react';

import { MAX_PRIMARY_ENTITY_SELECTIONS } from '@revelio/iso-utility';

import { useFilterContext } from '../../contexts/FilterProvider';
import type { ConsistencyKey } from '../../engine/filterGroupConsistency';
import { useRecentEntities } from '../../hooks/useRecentEntities';
import type {
  PrimaryFilterId,
  TreeFilter,
  TreeSelection,
} from '../../types/filters';
import { getPrimarySelectionMatchKey } from '../../utils/primaryFilterUtils';
import { TreeFilterForm } from './TreeFilterForm';

type PrimaryFilterFormRenderProps = {
  primaryFilter: TreeFilter | null;
  close: () => void;
  selectedFilterId: PrimaryFilterId;
  isOpen?: boolean;
  searchInputRef?: RefObject<HTMLInputElement>;
  isSelfSignupUser?: boolean;
  disallowMultiLevelSelect?: boolean;
  groupKeyOverride?: ConsistencyKey;
  selectionLimit?: number;
  disabledLevels?: number[];
  showSelectedValues?: boolean;
  currentTreeSelections?: TreeSelection[];
  replaceSingleSelections?: boolean;
  allowMultiLevelSelect?: boolean;
};

export const PrimaryFilterFormRender = ({
  primaryFilter,
  close,
  selectedFilterId,
  isOpen,
  searchInputRef,
  isSelfSignupUser,
  disallowMultiLevelSelect = false,
  groupKeyOverride,
  selectionLimit: selectionLimitProp,
  disabledLevels,
  showSelectedValues = false,
  currentTreeSelections,
  replaceSingleSelections = false,
  allowMultiLevelSelect,
}: PrimaryFilterFormRenderProps) => {
  const { dispatch: filterDispatch } = useFilterContext();
  const { addRecents } = useRecentEntities(selectedFilterId);

  // An explicit selectionLimit prop always wins (single-entity report
  // parameter pickers rely on it); everything else gets the global cap.
  const selectionLimit = selectionLimitProp ?? MAX_PRIMARY_ENTITY_SELECTIONS;

  const handleSavePrimaryTreeFilter = (updatedFilter: TreeFilter) => {
    const selectedKeys = new Set(
      updatedFilter.selections.map(getPrimarySelectionMatchKey)
    );
    const deselected = (primaryFilter?.primarySelections ?? []).filter(
      (selection) => !selectedKeys.has(getPrimarySelectionMatchKey(selection))
    );
    addRecents([...deselected, ...updatedFilter.selections]);

    filterDispatch({
      type: 'UPDATE_PRIMARY_SELECTIONS',
      payload: {
        filterId: updatedFilter.id,
        newSelections: updatedFilter.selections,
        limit: selectionLimit,
        groupKeyOverride,
        // New chips are appended after the existing ones so the first
        // selection keeps its position.
        appendLast: true,
      },
    });
  };

  if (!primaryFilter) return null;
  return (
    <TreeFilterForm
      filter={primaryFilter || null}
      disallowMultiLevelSelect={
        allowMultiLevelSelect ? false : disallowMultiLevelSelect
      }
      closeMenu={close}
      isOpen={isOpen}
      searchInputRef={searchInputRef}
      isSelfSignupUser={isSelfSignupUser}
      onSave={handleSavePrimaryTreeFilter}
      disabledLevels={disabledLevels}
      maxSelections={selectionLimit}
      showSelectedValues={showSelectedValues}
      currentTreeSelections={currentTreeSelections}
      replaceSingleSelections={replaceSingleSelections}
    />
  );
};
